import { Box, Button, CircularProgress, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Paper, Select, Snackbar, styled, TextField, Typography, useMediaQuery, useTheme, Alert } from '@mui/material';
import React, { useContext, useState } from 'react';
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import useUserId from 'hooks/useUserId';
import colors from 'colors/color';
import edit_icon from '../../assets/img/edit_icon.svg';
import cancel_icon from '../../assets/img/cancel_icon.svg';
import opened_eye_icon from '../../assets/img/open_eye_icon.svg';
import closed_eye_icon from '../../assets/img/closed_eye_icon.svg';
import SnackbarContext from 'components/snackBar/SnackbarContext';

const CancelButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#fff",
    color: "#FF0000",
    borderRadius: "10px",
    textTransform: "none",
    border: "1px solid #FF0000",
    boxShadow: "0px 4px 50px 0px #0000001A",
    px: 3,
    py: 1.5,
    fontWeight: "500",
    fontFamily: "Poppins",
    minWidth: "120px",
    height: "40px",
    "&:hover": {
        backgroundColor: "#FF0000",
        color: "#fff",
    },
    "&.Mui-disabled": {
        backgroundColor: "#e0e0e0",
        color: "#999",
        border: "1px solid #e0e0e0",
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
    },
}));

const UpdateButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#fff",
    color: "#004F98",
    borderRadius: "10px",
    textTransform: "none",
    boxShadow: "0px 4px 50px 0px #0000001A",
    px: 3,
    py: 1.5,
    fontWeight: "500",
    fontFamily: "Poppins",
    minWidth: "180px",
    height: "40px",
    "&:hover": {
        backgroundColor: "#0085FF",
        color: "#fff",
    },
    "&.Mui-disabled": {
        backgroundColor: "#e0e0e0",
        color: "#999",
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
    },
}));

const StyledTextField = styled(TextField)(({ theme, isediting }) => ({
    width: "100%",
    maxWidth: 400,
    height: 48,
    gap: "10px",
    borderRadius: "10px",
    border: "1px",
    paddingTop: "15px",
    paddingRight: "20px",
    paddingBottom: "15px",
    paddingLeft: "20px",

    "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: "#fff",
        borderColor: "#D9D9D9",
    },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
    color: "#000",
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: '14px',
    [theme.breakpoints.down('sm')]: {
        marginBottom: '8px',
    },
}));

const StyledSelect = styled(Select)(({ theme, isediting }) => ({
    height: 56,
    fontFamily: "Poppins",
    borderRadius: "10px",
    ...(isediting === "true" && {
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#e0e0e0",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "transparent",
            borderWidth: "2px",
            boxShadow: "0px 0px 5px 0.4px #0085FF",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "transparent",
            borderWidth: "2px",
            boxShadow: "0px 0px 5px 0.4px #0085FF",
        },
    }),
    ...(isediting === "false" && {
        backgroundColor: "#fff",
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#00000033",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#00000033",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "transparent",
        },
    }),
    "& .MuiSelect-select": {
        padding: "16.5px 14px",
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
    },
}));

const StyledDatePicker = styled(DatePicker)(({ theme, isediting }) => ({
    "& .MuiOutlinedInput-root": {
        height: 56,
        borderRadius: "10px",
        fontFamily: "Poppins",
        ...(isediting === "true" && {
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent",
                borderWidth: "2px",
                boxShadow: "0px 0px 5px 0.4px #0085FF",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent",
                borderWidth: "2px",
                boxShadow: "0px 0px 5px 0.4px #0085FF",
            },
        }),
        ...(isediting === "false" && {
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#00000033",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#00000033",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent",
            },
        }),
    },
}));

function RecruiterPasswordSettings({ formData, setFormData }) {
    const { showSnackbar } = useContext(SnackbarContext);
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const userId = useUserId();
    const [isEditingBio, setIsEditingBio] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [severity, setSeverity] = useState("success");
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [passwordValues, setPasswordValues] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
    });

    const allPasswordFieldsEmpty = 
        !passwordValues.currentPassword && 
        !passwordValues.newPassword && 
        !passwordValues.confirmPassword;

    // const showSnackbar = (message, severity = "info") => {
    //     setSnackbar({ open: true, message, severity });
    // };

    const validatePasswords = () => {
        const { currentPassword, newPassword, confirmPassword } = passwordValues;
        if (!currentPassword.trim()) return t("personalInformation.password_requirements.required");
        if (!newPassword.trim()) return t("personalInformation.password_requirements.new_required");
        if (newPassword.length < 8) return t("personalInformation.password_requirements.min_length");
        if (!/[A-Za-z]/.test(newPassword)) return t("personalInformation.password_requirements.must_contain_letters");
        if (!/[0-9]/.test(newPassword)) return t("personalInformation.password_requirements.must_contain_numbers");
        if (newPassword !== confirmPassword) return t("personalInformation.password_requirements.passwords_dont_match");
        return null;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (["currentPassword", "newPassword", "confirmPassword"].includes(name)) {
            setPasswordValues((prev) => ({
                ...prev,
                [name]: value,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleEdit = () => setIsEditing(true);

    const toggleCurrentPasswordVisibility = () => {
        setShowCurrentPassword(!showCurrentPassword);
    };

    const toggleNewPasswordVisibility = () => {
        setShowNewPassword(!showNewPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const error = validatePasswords();
        if (error) {
            showNotification(error, "error");
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}authentication/change-password/`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                    },
                    body: JSON.stringify({
                        user: userId.userId,
                        old_password: passwordValues.currentPassword,
                        new_password: passwordValues.newPassword,
                        // confirm_password: passwordValues.confirmPassword,
                    }),
                }
            );
            const data = await response.json();
            console.log("Response212:", data.message);
            if (response.ok) {
                showNotification(t("personalInformation.password_updated"), "success");
                setIsEditing(false);
                setPasswordValues({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: ""
                });
            } else {
                // Check if the error is about incorrect old password
                if (data.message === 'Old password is incorrect') {
                    showNotification(data.message, "error");
                } else {
                    showNotification(data.message || t("personalInformation.password_update_failed"), "error");
                }
            }
        } catch (error) {
            console.error("Error updating password:", error);
            showNotification(error.message || t("personalInformation.password_update_failed"), "error");
        } finally {
            setLoading(false);
        }
    };

    const showNotification = (message, severity = "success") => {
        setAlertMessage(message);
        setSeverity(severity);
        showSnackbar(message, { severity: severity });    };

    const handleCancel = () => {
        setIsEditing(false);
        setPasswordValues({
            currentPassword: "",
            newPassword: "",
            confirmPassword: ""
        });
    };

    return (
        <Box sx={{ width: '100%', p: { xs: 1, sm: 2, md: 3 }}}>
            <Grid
                container
                spacing={{ xs: 2, md: 3 }}
                sx={{ width: '100%' }}
            >
                <Grid container item xs={12} spacing={{ xs: 1, sm: 2 }} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <StyledTypography sx={{ color: "grey", opacity: "80%", fontSize: "14px"}}>
                            {t("personalInformation.current_password")}
                        </StyledTypography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={6} lg={4}>
                        <StyledTextField
                            fullWidth
                            required
                            id="currentPassword"
                            name="currentPassword"
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={passwordValues.currentPassword}
                            onChange={handleInputChange}
                            isediting={isEditing.toString()}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={toggleCurrentPasswordVisibility}
                                            edge="end"
                                            sx={{ marginRight: 1 }}
                                        >
                                            <img
                                                src={showCurrentPassword ? opened_eye_icon : closed_eye_icon}
                                                alt={showCurrentPassword ? "Hide password" : "Show password"}
                                                style={{
                                                    width: '15px',
                                                    height: '15px',
                                                    filter: 'none',
                                                    opacity: 1
                                                }}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>

                <Grid container item xs={12} spacing={{ xs: 1, sm: 2 }} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <StyledTypography sx={{ color: "grey", opacity: "80%", fontSize: "14px"}}>
                            {t("personalInformation.new_password")}
                            
                        </StyledTypography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={6} lg={4}>
                        <StyledTextField
                            fullWidth
                            required
                            id="newPassword"
                            name="newPassword"
                            type={showNewPassword ? 'text' : 'password'}
                            value={passwordValues.newPassword}
                            onChange={handleInputChange}
                            isediting={isEditing.toString()}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={toggleNewPasswordVisibility}
                                            edge="end"
                                            sx={{ marginRight: 1 }}
                                        >
                                            <img
                                                src={showNewPassword ? opened_eye_icon : closed_eye_icon}
                                                alt={showNewPassword ? "Hide password" : "Show password"}
                                                style={{
                                                    width: '15px',
                                                    height: '15px',
                                                    filter: 'none',
                                                    opacity: 1
                                                }}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>

                {/* Password Requirements Alert */}
                {/* <Grid container item xs={12} > */}
    
                    {/* <Grid item xs={12} sm={8} md={6} lg={4}>
                        <Alert 
                            severity="info" 
                            sx={{
                                mt: 1,
                                backgroundColor: '#e3f2fd',
                                borderRadius: '8px',
                                '& .MuiAlert-message': {
                                    fontSize: '12px',
                                    fontFamily: 'Poppins',
                                    color: '#1976d2'
                                },
                                '& .MuiAlert-icon': {
                                    fontSize: '16px'
                                }
                            }}
                        >
                            {t("personalInformation.password_hint") || "Password must contain at least 8 characters including letters and numbers"}
                        </Alert>
                    </Grid> */}
                {/* </Grid> */}



                <Grid container item xs={12}  alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <StyledTypography sx={{ color: "grey", opacity: "80%", fontSize: "14px"}}>
                            {t("personalInformation.confirm_new_password")}
                        </StyledTypography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={6} lg={4}>
                        <StyledTextField
                            fullWidth
                            required
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={passwordValues.confirmPassword}
                            onChange={handleInputChange}
                            isediting={isEditing.toString()}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={toggleConfirmPasswordVisibility}
                                            edge="end"
                                            sx={{ marginRight: 1 }}
                                        >
                                            <img
                                                src={showConfirmPassword ? opened_eye_icon : closed_eye_icon}
                                                alt={showConfirmPassword ? "Hide password" : "Show password"}
                                                style={{
                                                    width: '15px',
                                                    height: '15px',
                                                    filter: 'none',
                                                    opacity: 1
                                                }}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Box sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 2,
                gap: 2,
                mr: -3
            }}>
                <CancelButton
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={allPasswordFieldsEmpty}
                >
                    {t("general.cancel")}
                </CancelButton>
                <UpdateButton
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !!validatePasswords()}
                >
                    {loading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CircularProgress size={20} color="inherit" />
                            <span>{t("personalInformation.updating")}</span>
                        </Box>
                    ) : (
                        t("personalInformation.change_password")
                    )}
                </UpdateButton>
            </Box>
        </Box>
    );
}

export default RecruiterPasswordSettings;